import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "./features/auth";
import { RollbarProvider } from "@/providers/RollbarProvider";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
// Keep layout and shared UI in the main bundle — they're needed immediately
import AppLayout from "./layout/AppLayout";
import { ScrollToTop } from "./components/common/ScrollToTop";
import LoadingState from "./components/common/LoadingState";
import { PostHogUserIdentify } from "@/providers/PostHogUserIdentify";
import { isChatEnabled } from "@/config/features";

// Protected pages — loaded on demand per route
const Home = lazy(() => import("./pages/Dashboard/Home"));
const PropertiesList = lazy(() => import("./pages/Properties/PropertiesList"));
const PropertyCreate = lazy(() => import("./pages/Properties/PropertyCreate"));
const PropertyEdit = lazy(() => import("./pages/Properties/PropertyEdit"));
const PropertyDetailsEdit = lazy(
	() => import("./pages/Properties/PropertyDetailsEdit"),
);
const PropertyView = lazy(() => import("./pages/Properties/PropertyView"));
const CalcularAlquilerView = lazy(
	() => import("./pages/Properties/CalcularAlquilerView"),
);
const CalcularVentaView = lazy(
	() => import("./pages/Properties/CalcularVentaView"),
);
const MortgageCreate = lazy(() => import("./pages/Mortgages/MortgageCreate"));
const MortgageEdit = lazy(() => import("./pages/Mortgages/MortgageEdit"));
const UserProfiles = lazy(() => import("./pages/UserProfiles"));
const Settings = lazy(() => import("./pages/Settings"));
const Pricing = lazy(() => import("./pages/Subscriptions/Pricing"));
const Blank = lazy(() => import("./pages/Blank"));
const Chat = lazy(() => import("./features/chat/pages/ChatView"));
const NewCalculation = lazy(
	() => import("./features/new-calculation/pages/NewCalculationView"),
);
const Valuar = lazy(() => import("./features/valuar/pages/ValuarView"));
const ValuarCalculator = lazy(
	() => import("./features/valuar/pages/ValuarCalculatorView"),
);

// Auth pages — public, bundled separately from the app shell
const SignIn = lazy(() => import("./pages/AuthPages/SignIn"));
const SignUp = lazy(() => import("./pages/AuthPages/SignUp"));
const PasswordReset = lazy(() => import("./pages/AuthPages/PasswordReset"));
const UpdatePassword = lazy(() => import("./pages/AuthPages/UpdatePassword"));
const NotFound = lazy(() => import("./pages/OtherPage/NotFound"));

import "./i18n";

const PageLoader = (
	<div className="flex h-screen items-center justify-center">
		<LoadingState size="md" />
	</div>
);

export default function App() {
	return (
		<AuthProvider>
			<PostHogUserIdentify />
			<RollbarProvider>
				<TooltipProvider>
					<CurrencyProvider>
						<Router>
							<ScrollToTop />
							<Suspense fallback={PageLoader}>
								<Routes>
									{/* Dashboard Layout - Protected Routes */}
									<Route
										element={
											<ProtectedRoute>
												<AppLayout />
											</ProtectedRoute>
										}
									>
										{/* New calculation is the app's landing view */}
										<Route
											index
											path="/"
											element={<Navigate to="/new" replace />}
										/>

										{/* Business Pages */}
										<Route path="/new" element={<NewCalculation />} />
										<Route path="/valuar" element={<Valuar />} />
										<Route
											path="/valuar/alquiler"
											element={<ValuarCalculator kind="rent" />}
										/>
										<Route
											path="/valuar/venta"
											element={<ValuarCalculator kind="sale" />}
										/>
										<Route path="/properties" element={<PropertiesList />} />
										<Route
											path="/properties/create"
											element={<PropertyCreate />}
										/>
										<Route path="/properties/:id" element={<PropertyView />} />
										<Route
											path="/properties/:id/alquiler/calcular"
											element={<CalcularAlquilerView />}
										/>
										<Route
											path="/properties/:id/venta/calcular"
											element={<CalcularVentaView />}
										/>
										<Route
											path="/properties/:id/edit"
											element={<PropertyEdit />}
										/>
										<Route
											path="/properties/:id/detalles"
											element={<PropertyDetailsEdit />}
										/>
										<Route
											path="/mortgages/create"
											element={<MortgageCreate />}
										/>
										<Route
											path="/properties/:propId/mortgages/:id/edit"
											element={<MortgageEdit />}
										/>

										<Route path="/dashboard" element={<Home />} />

										{/* Others Page */}
										<Route path="/profile" element={<UserProfiles />} />
										<Route path="/settings" element={<Settings />} />
										<Route path="/pricing" element={<Pricing />} />
										{isChatEnabled() && <Route path="/chat" element={<Chat />} />}

										<Route path="/blank" element={<Blank />} />
									</Route>

									{/* Auth Layout - Public Routes */}
									<Route path="/signin" element={<SignIn />} />
									<Route path="/signup" element={<SignUp />} />
									<Route path="/reset-password" element={<PasswordReset />} />
									<Route path="/update-password" element={<UpdatePassword />} />

									{/* Fallback Route */}
									<Route path="*" element={<NotFound />} />
								</Routes>
							</Suspense>
						</Router>
					</CurrencyProvider>
				</TooltipProvider>
			</RollbarProvider>
		</AuthProvider>
	);
}
